// Group combat coordination: attack tokens (decision 0049; the suggestion "group melee: attack tokens"). A pool per
// target holds a capped number of tokens; only a holder may start an attack, so a crowd takes turns instead of
// swinging at once, and the rest circle. Agents ask each step with a priority and their distance; the pool grants
// free tokens to the best askers (higher priority first, then nearer, then lower id: deterministic). A token goes
// back when its holder's attack is done or interrupted, when the holder dies, or when it has held too long without
// using it (the game calls `release` for the first two; the pool times out the last). The cap is the difficulty's.

/** Why a token went back. */
export type ReleaseReason = "done" | "hit" | "death" | "timeout" | "cancel";

/** The pool's numbers (the game's). */
export interface TokenPoolOptions {
  /** Tokens at once (the difficulty's attack-token cap). */
  cap: number;
  /** Seconds a holder may keep a token. */
  maxHold: number;
  /** Seconds after a release before the same agent may be granted again (so others get a turn). */
  regrant: number;
}

/** A pool's state: plain data, saved with the game. */
export interface TokenPoolState {
  cap: number;
  /** Holder id → seconds held. */
  held: [number, number][];
  /** Agent id → seconds left before it may be granted again. */
  rest: [number, number][];
}

/** Counts since creation, for specs and the debug view. */
export interface TokenStats {
  granted: number;
  released: Record<ReleaseReason, number>;
  /** The most tokens out at once. */
  maxHeld: number;
}

/** A request this step. */
interface Ask {
  id: number;
  priority: number;
  distance: number;
}

/** Planted defects, beside what they break. */
export interface TokenFaults {
  /** The cap is ignored: every asker is granted (the bot fight's attackers-per-frame check must go red). */
  capRemoved: boolean;
}

/** A pool of attack tokens on one target. */
export class AttackTokens {
  readonly faults: TokenFaults = { capRemoved: false };
  private held = new Map<number, number>();
  private rest = new Map<number, number>();
  private asks: Ask[] = [];
  private counts: TokenStats = { granted: 0, released: { done: 0, hit: 0, death: 0, timeout: 0, cancel: 0 }, maxHeld: 0 };
  cap: number;

  constructor(readonly options: TokenPoolOptions) {
    this.cap = options.cap;
  }

  /** Ask for a token this step (call every step you want one; asks are cleared by `arbitrate`). */
  request(id: number, o: { priority?: number; distance: number }): void {
    if (this.held.has(id)) return;
    this.asks.push({ id, priority: o.priority ?? 0, distance: o.distance });
  }

  /** Whether `id` holds a token. */
  holds(id: number): boolean {
    return this.held.has(id);
  }

  /** The holders, by id. */
  holders(): number[] {
    return [...this.held.keys()].sort((a, b) => a - b);
  }

  /** Restart `id`'s hold timer (its attack just started: the token mustn't time out under a wind-up). */
  renew(id: number): void {
    if (this.held.has(id)) this.held.set(id, 0);
  }

  /** Give `id`'s token back (no-op when it holds none). */
  release(id: number, reason: ReleaseReason): void {
    if (!this.held.delete(id)) return;
    this.counts.released[reason]++;
    if (this.options.regrant > 0) this.rest.set(id, this.options.regrant);
  }

  /** Forget an agent entirely (it died or left): its token back, its rest cleared. */
  forget(id: number, reason: ReleaseReason = "death"): void {
    this.release(id, reason);
    this.rest.delete(id);
  }

  /** Once per step after every request: age holders (timing out the stale), then grant free tokens to the best askers.
   *  Returns who was granted and who timed out this step. */
  arbitrate(dt: number): { granted: number[]; timedOut: number[] } {
    const timedOut: number[] = [];
    for (const [id, t] of [...this.held].sort((a, b) => a[0] - b[0])) {
      const nt = t + dt;
      if (nt >= this.options.maxHold) {
        timedOut.push(id);
        this.release(id, "timeout");
      } else this.held.set(id, nt);
    }
    for (const [id, t] of [...this.rest]) {
      if (t - dt <= 0) this.rest.delete(id);
      else this.rest.set(id, t - dt);
    }
    const granted: number[] = [];
    const asks = this.asks.filter((a) => !this.rest.has(a.id)).sort((a, b) => b.priority - a.priority || a.distance - b.distance || a.id - b.id);
    for (const a of asks) {
      if (this.held.has(a.id)) continue;
      if (!this.faults.capRemoved && this.held.size >= this.cap) break;
      this.held.set(a.id, 0);
      granted.push(a.id);
      this.counts.granted++;
    }
    this.asks = [];
    this.counts.maxHeld = Math.max(this.counts.maxHeld, this.held.size);
    return { granted, timedOut };
  }

  /** Counts since creation. */
  stats(): TokenStats {
    return structuredClone(this.counts);
  }

  snapshot(): TokenPoolState {
    return { cap: this.cap, held: [...this.held].sort((a, b) => a[0] - b[0]), rest: [...this.rest].sort((a, b) => a[0] - b[0]) };
  }

  restore(s: TokenPoolState): void {
    this.cap = s.cap;
    this.held = new Map(s.held);
    this.rest = new Map(s.rest);
    this.asks = [];
  }
}
